import { vestResolver } from '@hookform/resolvers/vest'
import { IfpOccupation, PrimaryReasonForIfp } from '@shared/sec-loans'
import { ConsentPrivacyLink } from '@ui-common/components/ConsentPrivacyLink/ConsentPrivacyLink'
import { ImplicitlySubmittableForm } from '@ui-common/components/ImplicitlySubmittableForm'
import { useWizardForm } from '@ui-common/hooks'
import { ExternalLink } from '@ui-components/actions/ExternalLink'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { ModalButtons } from '@ui-components/containers/PromptModal'
import { DataSummaryItem } from '@ui-components/form/DataSummary'
import { FormDescribedCheckbox } from '@ui-components/form/FormDescribedCheckbox'
import { FormSectionContainer } from '@ui-components/form/FormSectionContainer'
import { FlexColumnContainer } from '@ui-components/global/CommonStyling'
import { SPACE_MEDIUM_PLUS } from '@ui-components/global/spacing'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  IFP_MAXIMUM_CHARGE_AMOUNT,
  IFP_MINIMUM_CHARGE_AMOUNT,
} from '../../../../common/steps/assistance-step/components/ifp-loans-dropdown/constants'
import { useAnalyticsService } from '../../../../state/analyticsContext'
import { IfpWizardData } from '../../../wizard/types'
import { createFormValidator } from './formValidator'

export interface ConsentFormData {
  consentHealthDataProcessing?: boolean
  confirmInformationAccuracy?: boolean
}

export const Consent = ({
  confirmConsent,
  cancelConsent,
  wizardData,
}: {
  confirmConsent: () => void
  cancelConsent: () => void
  wizardData: IfpWizardData
}) => {
  const { analyticsService } = useAnalyticsService()
  const { applicantInfos, basicInfo } = wizardData
  const primaryReason = basicInfo?.primaryReason
  const occupation = applicantInfos?.[0]?.occupation
  const shouldAskHealthInfoProcessing =
    primaryReason === PrimaryReasonForIfp.LongtermIllness || occupation === IfpOccupation.LongtermSickLeave
  const intl = useIntl()

  const methods = useWizardForm<ConsentFormData>({
    shouldUnregister: false,
    resolver: vestResolver(createFormValidator(intl.formatMessage, shouldAskHealthInfoProcessing)),
  })
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods

  const submit = () => {
    confirmConsent()
  }

  return (
    <ImplicitlySubmittableForm onSubmit={handleSubmit(submit)}>
      <FormSectionContainer>
        <SecondaryHeading
          header={intl.formatMessage({ id: 'summary-consent-title' })}
          bodyText={intl.formatMessage({ id: 'ifp-application-consent-info-text' })}
          additionalText={<ConsentPrivacyLink analyticsCategory="Consent" analyticsService={analyticsService} />}
        />
        <ConsentInfoSection>
          <DataSummaryItem
            id={'ifp-loans-price-info'}
            label={intl.formatMessage({ id: 'loans-assistance-ifp-loans-price-info-label' })}
            value={intl.formatMessage(
              { id: 'ifp-application-price-info-description' },
              {
                minimumChargeAmount: IFP_MINIMUM_CHARGE_AMOUNT,
                maximumChargeAmount: IFP_MAXIMUM_CHARGE_AMOUNT,
                servicePricingLink: (
                  <ExternalLink href={intl.formatMessage({ id: 'common-service-pricing-link' })}>
                    {intl.formatMessage({ id: 'ifp-application-service-pricing-link-label' })}
                  </ExternalLink>
                ),
              },
            )}
          />
          <DataSummaryItem
            id={'ifp-loans-contract-info'}
            label={intl.formatMessage({ id: 'ifp-application-consent-contract-label' })}
            value={intl.formatMessage({ id: 'ifp-application-consent-contract-description' })}
          />
        </ConsentInfoSection>
        {shouldAskHealthInfoProcessing && (
          <FormDescribedCheckbox
            isOneColumnLayout={true}
            id="consent-health-data-processing-checkbox"
            description={intl.formatMessage({ id: 'ifp-application-consent-health-data-processing-label' })}
            checkboxLabel={intl.formatMessage({ id: 'common-yes' })}
            side="right"
            required={true}
            error={errors.consentHealthDataProcessing?.message}
            {...register('consentHealthDataProcessing')}
          />
        )}
        <FormDescribedCheckbox
          isOneColumnLayout={true}
          id="confirm-information-accuracy-checkbox"
          description={intl.formatMessage({ id: 'ifp-application-consent-confirm-information-label' })}
          checkboxLabel={intl.formatMessage({ id: 'common-yes' })}
          side="right"
          required={true}
          error={errors.confirmInformationAccuracy?.message}
          {...register('confirmInformationAccuracy')}
        />
        <ModalButtons
          mode={ModalVisualMode.Plain}
          primaryButtonLabel={intl.formatMessage({ id: 'ifp-application-approve-with-identification-label' })}
          secondaryButtonLabel={intl.formatMessage({ id: 'common-cancel' })}
          onPrimaryButtonClick={handleSubmit(submit)}
          onSecondaryButtonClick={cancelConsent}
        />
      </FormSectionContainer>
    </ImplicitlySubmittableForm>
  )
}

const ConsentInfoSection = styled(FlexColumnContainer)`
  padding: 0;
  gap: ${SPACE_MEDIUM_PLUS};
  margin-bottom: 0px;
`
